import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  ClipboardCheck, 
  FileText, 
  CreditCard, 
  CheckCircle, 
  ArrowRight, 
  Shield, 
  Zap, 
  Clock, 
  Smartphone, 
  User, 
  MessageSquare,
  Sparkles,
  Database,
  Scan,
  Check
} from 'lucide-react';
import { Link } from 'react-router-dom';

const MorganPatientIntakePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Morgan, Patient Intake Specialist</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Meet Morgan, Your
                <span className="bg-gradient-to-r from-blue-900 via-blue-600 to-blue-400 bg-clip-text text-transparent"> AI Patient Intake Agent</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                Morgan proactively chases down intake forms and insurance cards so your front desk doesn't have to. Using intelligent voice and SMS, she guides patients through the process before they walk in the door.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-200">
                    Get a Demo
                  </Button>
                </Link>
                <Button variant="outline" className="px-8 py-6 rounded-xl text-lg border-2 hover:bg-gray-50">
                  See Pricing
                </Button>
              </div>
            </div>
            
            {/* Right: Visual */}
            <div className="relative h-[600px] w-full flex items-center justify-center">
                <div className="relative w-full max-w-lg">
                    {/* Main Card */}
                    <div className="bg-white rounded-[2rem] shadow-2xl p-6 border border-slate-100 relative z-10">
                        {/* Header with Agent */}
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 p-1">
                                <img src="/assets/morgan.png" alt="Morgan" className="w-full h-full rounded-full object-cover" />
                            </div>
                            <div>
                                <h3 className="font-bold text-xl text-slate-900">Morgan</h3>
                                <div className="flex items-center gap-2 text-sm text-blue-600 font-medium">
                                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                    Intake Specialist
                                </div>
                            </div>
                        </div>

                        {/* Interactive Chat Bubble */}
                        <div className="space-y-4 mb-6">
                            <div className="flex gap-3">
                                <div className="bg-slate-100 rounded-2xl rounded-tl-none p-4 text-sm text-slate-700 max-w-[85%]">
                                    Hi Sarah! Just a reminder to upload your insurance card before your visit tomorrow. It only takes a second! 📸
                                </div>
                            </div>
                            <div className="flex gap-3 flex-row-reverse">
                                <div className="bg-blue-600 rounded-2xl rounded-tr-none p-4 text-white text-sm max-w-[85%] shadow-md">
                                    <div className="flex items-center gap-2 mb-2 bg-blue-500/30 p-2 rounded-lg border border-blue-400/30">
                                        <CreditCard className="w-4 h-4 text-blue-100" />
                                        <span className="text-xs font-medium">insurance_card.jpg</span>
                                    </div>
                                    Done! Here it is.
                                </div>
                            </div>
                        </div>

                        {/* Processing Status */}
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Processing Data</span>
                                <span className="text-xs font-bold text-green-600">100% Complete</span>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm text-slate-700">
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    <span>Member ID Extracted: <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-slate-200 text-xs">XJ992811</span></span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-slate-700">
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    <span>Group Number Verified</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-slate-700">
                                    <Zap className="w-4 h-4 text-blue-500" />
                                    <span>Saved in Asha PMS</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Floating Elements */}
                    <div className="absolute -right-8 top-20 bg-white p-4 rounded-xl shadow-lg border border-slate-100 animate-bounce">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                <Scan className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">OCR Accuracy</div>
                                <div className="text-lg font-bold text-slate-900">99.8%</div>
                            </div>
                        </div>
                    </div>

                    <div className="absolute -left-4 bottom-32 bg-white p-4 rounded-xl shadow-lg border border-slate-100">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <FileText className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Forms Completed</div>
                                <div className="text-lg font-bold text-slate-900">Before Arrival</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Complete Intake Automation
            </h2>
            <p className="text-lg text-gray-600">
              Morgan handles the tedious pre-visit work so your team can focus on the patient experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform">
                <FileText className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">100% Form Completion</h3>
              <p className="text-gray-600">
                Morgan chases patients via SMS and voice until forms are done. No more clipboards in the waiting room.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-purple-600 group-hover:scale-110 transition-transform">
                <CreditCard className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Instant Card Capture</h3>
              <p className="text-gray-600">
                Patients can text a photo of their insurance card. Morgan extracts the data and attaches it to the patient file.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-green-600 group-hover:scale-110 transition-transform">
                <Shield className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Seamless Handoff</h3>
              <p className="text-gray-600">
                Once captured, insurance data is instantly passed to Amanda for full benefit verification.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Visualization */}
      <section className="py-24 px-6 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-400 font-bold tracking-wider text-xs uppercase mb-4">
                <Zap className="w-4 h-4" />
                Intelligent Workflow
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                How Morgan Works
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                It starts the moment an appointment is booked. Morgan monitors your schedule and identifies patients with missing information.
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Scans Schedule</h4>
                    <p className="text-slate-400 text-sm">Identifies new patients or existing patients with expired forms.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Smart Outreach</h4>
                    <p className="text-slate-400 text-sm">Sends a personalized SMS link. If ignored, she follows up with a friendly voice call.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Data Extraction</h4>
                    <p className="text-slate-400 text-sm">Reads uploaded images and PDFs, extracting key data points automatically.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 font-bold">4</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Asha PMS Update</h4>
                    <p className="text-slate-400 text-sm">Writes everything directly into Asha PMS after migration.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
               <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 border border-slate-700 shadow-2xl">
                  <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
                              <MessageSquare className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                              <div className="font-bold text-white">Live Interaction</div>
                              <div className="text-xs text-slate-400">SMS Chat</div>
                          </div>
                      </div>
                      <div className="text-xs font-mono text-slate-500">Now</div>
                  </div>

                  <div className="space-y-4">
                      {/* Agent Msg */}
                      <div className="flex gap-4">
                          <div className="w-8 h-8 rounded-full bg-slate-700 flex-shrink-0 overflow-hidden border border-slate-600">
                              <img src="/assets/morgan.png" className="w-full h-full object-cover" alt="Morgan" />
                          </div>
                          <div className="bg-slate-700 p-3 rounded-2xl rounded-tl-none text-sm text-slate-200">
                              Hi John, just a reminder to upload your insurance card before your visit tomorrow! 📸
                          </div>
                      </div>

                      {/* User Msg */}
                      <div className="flex gap-4 flex-row-reverse">
                          <div className="w-8 h-8 rounded-full bg-slate-600 flex-shrink-0 flex items-center justify-center font-bold text-xs text-slate-300">J</div>
                          <div className="bg-blue-600 p-3 rounded-2xl rounded-tr-none text-sm text-white">
                              <div className="flex items-center gap-2 mb-2 bg-blue-500/30 p-2 rounded-lg">
                                  <CreditCard className="w-4 h-4" />
                                  <span className="text-xs">card_photo.jpg</span>
                              </div>
                              Here it is!
                          </div>
                      </div>

                      {/* System Msg */}
                      <div className="flex justify-center my-4">
                          <div className="bg-green-500/10 text-green-400 text-xs px-3 py-1 rounded-full border border-green-500/20 flex items-center gap-2">
                              <CheckCircle className="w-3 h-3" />
                              Data Extracted & Verified
                          </div>
                      </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Stats */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">15 min</div>
              <div className="text-gray-600 font-medium">Saved Per Patient</div>
            </div>
            <div className="bg-indigo-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-indigo-600 mb-2">98%</div>
              <div className="text-gray-600 font-medium">Pre-Arrival Completion</div>
            </div>
            <div className="bg-purple-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">0</div>
              <div className="text-gray-600 font-medium">Clipboards Used</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Stop chasing paperwork.
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Let Morgan handle the intake process so your front desk can focus on welcoming patients.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-demo">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 rounded-full text-lg shadow-xl shadow-blue-200 hover:shadow-blue-300 hover:-translate-y-1 transition-all">
                Start Automating Intake
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MorganPatientIntakePage;
